type Particle = {
  x: number;
  y: number;
  velocity: number;
  angle: number;
  gravity: number;
  decay: number;
  life: number;
  color: string;
  width: number;
  height: number;
  // Wobble physics for flutter effect
  wobble: number;
  wobbleSpeed: number;
  wobbleX: number;
  wobbleY: number;
  // Rotation/tilt animation
  tilt: number;
  tiltAngle: number;
  tiltSpeed: number;
  // Shape and size variation
  shape: 'square' | 'circle' | 'star';
  scalar: number;
  // Drift for horizontal wind effect
  drift: number;
};

type ConfettiOptions = {
  particleCount?: number;
  spread?: number;
  startVelocity?: number;
  gravity?: number;
  decay?: number;
  origin: { x: number; y: number };
  colors?: Array<string>;
  angle?: number;
};

const defaultColors = [
  '#ff6b6b',
  '#4ecdc4',
  '#45b7d1',
  '#96ceb4',
  '#feca57',
  '#ff9ff3',
  '#ff9500',
  '#a55eea',
  '#ff6348',
  '#1dd1a1',
  '#ff7675',
  '#74b9ff',
  '#fd79a8',
  '#fdcb6e',
  '#6c5ce7',
  '#e17055',
  '#00b894',
  '#0984e3',
  '#a29bfe',
  '#fd79a8',
  '#e84393',
  '#00cec9',
  '#6c5ce7',
  '#fdcb6e',
  '#e17055',
  '#00b894',
  '#0984e3',
  '#ff7675',
];

// Shape rendering functions
const drawSquare = (ctx: CanvasRenderingContext2D, width: number, height: number): void => {
  ctx.fillRect(-width / 2, -height / 2, width, height);
};

const drawCircle = (ctx: CanvasRenderingContext2D, width: number, height: number): void => {
  const radius = Math.min(width, height) / 2;
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, 2 * Math.PI);
  ctx.fill();
};

const drawStar = (ctx: CanvasRenderingContext2D, width: number, height: number): void => {
  const radius = Math.min(width, height) / 2;
  const spikes = 5;
  const outerRadius = radius;
  const innerRadius = radius * 0.4;

  let rotation = (Math.PI / 2) * 3;
  let x = 0;
  let y = 0;
  const step = Math.PI / spikes;

  ctx.beginPath();
  ctx.moveTo(0, -outerRadius);

  for (let i = 0; i < spikes; i++) {
    x = Math.cos(rotation) * outerRadius;
    y = Math.sin(rotation) * outerRadius;
    ctx.lineTo(x, y);
    rotation += step;

    x = Math.cos(rotation) * innerRadius;
    y = Math.sin(rotation) * innerRadius;
    ctx.lineTo(x, y);
    rotation += step;
  }

  ctx.lineTo(0, -outerRadius);
  ctx.closePath();
  ctx.fill();
};

const computeAngleTowardCenter = (origin: { x: number; y: number }): number => {
  const cx = window.innerWidth / 2;
  const cy = window.innerHeight / 2;
  const dx = cx - origin.x;
  const dy = cy - origin.y;

  // If origin is near viewport center, shoot straight up
  if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
    return -90;
  }

  return (Math.atan2(dy, dx) * 180) / Math.PI;
};

export const triggerCannonConfetti = (options: ConfettiOptions): void => {
  // Check if user prefers reduced motion and skip confetti if so
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const {
    particleCount = 300,
    spread = 90,
    startVelocity = 120, // Increased for higher burst reaching near top of viewport
    gravity = 1.0, // Increased for more realistic fall
    decay = 0.85, // Slightly faster decay like canvas-confetti
    origin,
    colors = defaultColors,
  } = options;

  // If no explicit angle, shoot toward viewport center
  const angleDeg = options.angle ?? computeAngleTowardCenter(origin);

  // Create canvas element
  const canvas = document.createElement('canvas');
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  canvas.style.position = 'fixed';
  canvas.style.top = '0';
  canvas.style.left = '0';
  canvas.style.pointerEvents = 'none';
  canvas.style.zIndex = '2147483647';
  canvas.setAttribute('data-confetti-canvas', '');
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    document.body.removeChild(canvas);
    return;
  }

  // Convert spread from degrees to radians
  const spreadRad = (spread * Math.PI) / 180;

  // Create particles array
  const particles: Array<Particle> = [];

  // Initialize particles
  const shapes: Array<'square' | 'circle' | 'star'> = ['square', 'circle', 'star'];

  for (let i = 0; i < particleCount; i++) {
    const velocity = startVelocity * (0.5 + Math.random() * 0.5);
    const baseAngle = (angleDeg * Math.PI) / 180;
    const angle = baseAngle + (spreadRad / 2 - Math.random() * spreadRad);
    const scalar = Math.random() * 0.6 + 0.4; // 0.4 to 1.0 size multiplier

    particles.push({
      x: origin.x,
      y: origin.y,
      velocity,
      angle,
      gravity,
      decay,
      life: 0,
      color: colors[Math.floor(Math.random() * colors.length)],
      width: (8 + Math.random() * 8) * scalar, // 8-16px width with scalar
      height: (4 + Math.random() * 6) * scalar, // 4-10px height with scalar
      // Wobble physics
      wobble: Math.random() * 10, // Initial wobble phase
      wobbleSpeed: 0.05 + Math.random() * 0.05, // 0.05-0.1 wobble speed
      wobbleX: origin.x,
      wobbleY: origin.y,
      // Rotation/tilt
      tilt: Math.random() * Math.PI, // Random initial tilt
      tiltAngle: Math.random() * Math.PI * 0.4 + Math.PI * 0.1, // 0.1π to 0.5π
      tiltSpeed: (Math.random() * 0.02 + 0.01) * (Math.random() > 0.5 ? 1 : -1), // ±0.01-0.03
      // Shape and variation
      shape: shapes[Math.floor(Math.random() * shapes.length)],
      scalar,
      // Wind drift
      drift: (Math.random() - 0.5) * 0.4, // -0.2 to 0.2 horizontal drift
    });
  }

  let animationId: number;

  const animate = () => {
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Update and draw particles
    for (let i = particles.length - 1; i >= 0; i--) {
      const particle = particles[i];

      // Update basic physics
      particle.x += Math.cos(particle.angle) * particle.velocity + particle.drift;
      particle.y += Math.sin(particle.angle) * particle.velocity + particle.gravity;
      particle.velocity *= particle.decay;
      particle.life += 0.016; // Approximately 1/60th of a second

      // Update wobble physics
      particle.wobble += particle.wobbleSpeed;
      particle.wobbleX = particle.x + 10 * particle.scalar * Math.cos(particle.wobble);
      particle.wobbleY = particle.y + 10 * particle.scalar * Math.sin(particle.wobble);

      // Update rotation/tilt
      particle.tilt += particle.tiltSpeed;

      // Calculate opacity based on life (fade out over 6 seconds)
      const progress = Math.min(particle.life / 6, 1);
      const opacity = 1 - progress;

      // Remove particle if faded out or off screen
      if (opacity <= 0 || particle.y > canvas.height + 100) {
        particles.splice(i, 1);
        continue;
      }

      // Convert hex color to rgba with opacity
      let fillStyle: string;
      if (particle.color.startsWith('#')) {
        const hex = particle.color.slice(1);
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
      } else {
        // Assume it's already in rgb format, add opacity
        fillStyle = particle.color.replace(')', `, ${opacity})`).replace('rgb(', 'rgba(');
      }

      // Save canvas state for transformation
      ctx.save();

      // Move to particle position (using wobble coordinates)
      ctx.translate(particle.wobbleX, particle.wobbleY);

      // Apply rotation based on tilt
      ctx.rotate(particle.tilt);

      // Set fill style
      ctx.fillStyle = fillStyle;

      // Draw shape based on particle type
      switch (particle.shape) {
        case 'square':
          drawSquare(ctx, particle.width, particle.height);
          break;
        case 'circle':
          drawCircle(ctx, particle.width, particle.height);
          break;
        case 'star':
          drawStar(ctx, particle.width, particle.height);
          break;
      }

      // Restore canvas state
      ctx.restore();
    }

    // Continue animation if particles remain
    if (particles.length > 0) {
      animationId = requestAnimationFrame(animate);
    } else {
      // Clean up
      canvas.parentNode?.removeChild(canvas);
    }
  };

  // Start animation
  animate();

  // Cleanup after maximum duration (8 seconds)
  setTimeout(() => {
    if (animationId) {
      cancelAnimationFrame(animationId);
    }
    if (canvas.parentNode) {
      canvas.parentNode.removeChild(canvas);
    }
  }, 8000);
};
